import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { offersEndpoint } from "../constants";

const ClientOffers = () => {
  const [offers, setOffers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [selectedOffers, setSelectedOffers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const fetchOffers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${offersEndpoint}client/`, {
        headers: {
          'Authorization': `Token ${token}`,
        }
      });
      setOffers(response.data.results || response.data);
    } catch (err) {
      setError("Failed to load offers.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchOffers();
    }
  }, [token]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleStatusFilter = (e) => {
    setStatusFilter(e.target.value);
  };

  const toggleDropdown = (offerId) => {
    setDropdownOpen(dropdownOpen === offerId ? null : offerId);
  };

  const handleWithdrawOffer = async (offerId) => {
    if (window.confirm("Are you sure you want to withdraw this offer?")) {
      try {
        await axios.patch(`${offersEndpoint}${offerId}/`, {
          status: 'WITHDRAWN',
        }, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${token}`,
          }
        });
        fetchOffers();
        setDropdownOpen(null);
      } catch (err) {
        console.error("Failed to withdraw offer:", err);
        alert("Failed to withdraw offer. Please try again.");
      }
    }
  };

  const handleEditOffer = (offerId) => {
    navigate(`/edit_offer/${offerId}`);
  };

  const handleViewCandidate = (userId) => {
    navigate(`/user/${userId}`);
  };

  const handleSelectOffer = (offerId) => {
    setSelectedOffers(prev => 
      prev.includes(offerId) 
        ? prev.filter(id => id !== offerId)
        : [...prev, offerId]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedOffers([]);
    } else {
      setSelectedOffers(filteredOffers.map(offer => offer.id));
    }
    setSelectAll(!selectAll);
  };

  const handleBulkWithdraw = async () => {
    if (selectedOffers.length === 0) return;
    
    if (window.confirm(`Are you sure you want to withdraw ${selectedOffers.length} offer(s)?`)) {
      try {
        await Promise.all(
          selectedOffers.map(offerId => 
            axios.patch(`${offersEndpoint}${offerId}/`, {
              status: 'WITHDRAWN',
            }, {
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Token ${token}`,
              }
            })
          )
        );
        fetchOffers();
        setSelectedOffers([]);
        setSelectAll(false);
      } catch (err) {
        console.error("Failed to bulk withdraw offers:", err);
        alert("Failed to withdraw some offers. Please try again.");
      }
    }
  };

  const filteredOffers = useMemo(() => {
    let result = [...offers];

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(
        (offer) =>
          offer.job?.title?.toLowerCase().includes(searchLower) ||
          offer.applicant?.first_name?.toLowerCase().includes(searchLower) ||
          offer.applicant?.last_name?.toLowerCase().includes(searchLower) ||
          offer.applicant?.email?.toLowerCase().includes(searchLower)
      );
    }

    if (statusFilter) {
      result = result.filter(offer => offer.status === statusFilter);
    }

    return result;
  }, [offers, searchTerm, statusFilter]);

  useEffect(() => {
    const handleClickOutside = () => setDropdownOpen(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
    setSelectAll(selectedOffers.length === filteredOffers.length && filteredOffers.length > 0);
  }, [selectedOffers, filteredOffers]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      PENDING: 'bg-slate-100 text-slate-700 border-slate-300',
      ACCEPTED: 'bg-emerald-100 text-emerald-700 border-emerald-300',
      DECLINED: 'bg-slate-200 text-slate-600 border-slate-400',
      WITHDRAWN: 'bg-gray-100 text-gray-600 border-gray-300',
      CANCELLED: 'bg-slate-100 text-slate-600 border-slate-300',
      DRAFT: 'bg-cyan-100 text-cyan-700 border-cyan-300'
    };

    return statusClasses[status] || 'bg-gray-100 text-gray-600 border-gray-400';
  };

  const getStatusCounts = () => {
    const counts = {
      total: offers.length,
      pending: offers.filter(o => o.status === 'PENDING').length,
      accepted: offers.filter(o => o.status === 'ACCEPTED').length,
      declined: offers.filter(o => o.status === 'DECLINED').length,
      withdrawn: offers.filter(o => o.status === 'WITHDRAWN').length,
      cancelled: offers.filter(o => o.status === 'CANCELLED').length,
      draft: offers.filter(o => o.status === 'DRAFT').length
    };
    return counts;
  };

  const statusCounts = getStatusCounts();

  return (
    <div className="lg:p-8">
      <div className="mb-8">
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
          <span 
            onClick={() => navigate("/jobs")}
            className="hover:text-gray-700 cursor-pointer"
          >
            Jobs
          </span>
          <span>›</span>
          <span className="text-gray-900">Offers Sent</span>
        </div>
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Job Offers</h1>
            <p className="mt-1 text-sm text-gray-600">
              Manage offers you've sent to candidates.
            </p>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/jobs")}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Back to Jobs
            </button>
            <button
              onClick={() => navigate("/offer/create")}
              className="px-4 py-2 text-white bg-teal-600 border border-teal-600 rounded-lg hover:bg-teal-700"
            >
              Create Offer
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-8">
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="text-2xl font-semibold text-slate-900">{statusCounts.total}</div>
          <div className="text-sm text-gray-600">Total Offers</div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="text-2xl font-semibold text-slate-700">{statusCounts.pending}</div>
          <div className="text-sm text-gray-600">Pending</div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="text-2xl font-semibold text-emerald-600">{statusCounts.accepted}</div>
          <div className="text-sm text-gray-600">Accepted</div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="text-2xl font-semibold text-slate-600">{statusCounts.declined}</div>
          <div className="text-sm text-gray-600">Declined</div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="text-2xl font-semibold text-slate-600">{statusCounts.withdrawn}</div>
          <div className="text-sm text-gray-600">Withdrawn</div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="text-2xl font-semibold text-teal-600">{statusCounts.draft}</div>
          <div className="text-sm text-gray-600">Draft</div>
        </div>
      </div>

      {loading && <p className="text-gray-500">Loading offers...</p>}
      {error && <p className="text-red-500">{error}</p>}

      {!loading && !error && (
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="pt-3 px-5">
            <h3 className="text-sm font-semibold text-gray-900">Offers Sent</h3>
          </div>
          <div className="flex gap-3 items-end py-4 px-5 border-b border-gray-200">
            <h3 className="text-2xl text-gray-900 font-semibold">
              {filteredOffers.length}
            </h3>
            <div className="flex items-center gap-1 text-sm text-teal-600">
              <span>📤</span>
              <span className="text-gray-600 ml-1">offers sent</span>
            </div>
          </div>

          <div className="px-5 py-4 border-b border-gray-200">
            <div className="flex gap-4 items-center flex-wrap">
              <input
                type="text"
                placeholder="Search candidates or positions..."
                value={searchTerm}
                onChange={handleSearch}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
              <select
                value={statusFilter}
                onChange={handleStatusFilter}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              >
                <option value="">All Statuses</option>
                <option value="PENDING">Pending</option>
                <option value="ACCEPTED">Accepted</option>
                <option value="DECLINED">Declined</option>
                <option value="WITHDRAWN">Withdrawn</option>
                <option value="CANCELLED">Cancelled</option>
                <option value="DRAFT">Draft</option>
              </select>
              {selectedOffers.length > 0 && (
                <button
                  onClick={handleBulkWithdraw}
                  className="px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700"
                >
                  Withdraw Selected ({selectedOffers.length})
                </button>
              )}
            </div>
          </div>

          <div>
            <div className="px-5 py-3 grid md:grid-cols-9 grid-cols-5 border-b border-gray-200">
              <div className="flex gap-3 items-center md:col-span-3 col-span-2">
                <input 
                  type="checkbox" 
                  className="rounded border-gray-300"
                  checked={selectAll}
                  onChange={handleSelectAll}
                />
                <h5 className="text-gray-500 font-semibold">Candidate</h5>
              </div>
              <div className="grid md:grid-cols-6 grid-cols-3 md:col-span-6 col-span-3">
                <h5 className="text-gray-500 font-semibold text-xs">Position</h5>
                <h5 className="text-gray-500 font-semibold text-xs hidden md:block">Email</h5>
                <h5 className="text-gray-500 font-semibold text-xs">Salary</h5>
                <h5 className="text-gray-500 font-semibold text-xs hidden md:block">Service</h5>
                <h5 className="text-gray-500 font-semibold text-xs">Status</h5>
                <h5 className="text-gray-500 font-semibold text-xs hidden md:block">Date Sent</h5>
              </div>
            </div>

            {filteredOffers.length === 0 ? (
              <div className="p-8 text-center">
                <div className="text-gray-400 text-6xl mb-4">📤</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No offers sent yet</h3>
                <p className="text-gray-600">Start by creating offers for your job applicants.</p>
              </div>
            ) : (
              filteredOffers.map((offer) => (
                <div key={offer.id} className="grid md:grid-cols-9 grid-cols-5 items-center p-4 border-b border-gray-200 hover:bg-gray-50">
                  <div className="flex items-center gap-3 md:col-span-3 col-span-2">
                    <input 
                      type="checkbox" 
                      className="rounded border-gray-300"
                      checked={selectedOffers.includes(offer.id)}
                      onChange={() => handleSelectOffer(offer.id)}
                    />
                    <div>
                      <p 
                        className="md:font-medium font-bold text-sm text-gray-900 cursor-pointer hover:text-teal-600"
                        onClick={() => handleViewCandidate(offer.applicant?.id)}
                      >
                        {offer.applicant?.first_name} {offer.applicant?.last_name}
                      </p>
                      <p className="text-xs text-gray-500 md:hidden">
                        {offer.job?.title}
                      </p>
                    </div>
                  </div>
                  <div className="hidden md:block">
                    <p className="font-medium text-xs text-gray-900">
                      {offer.job?.title}
                    </p>
                    <p className="text-xs text-gray-500">
                      {offer.job?.company}
                    </p>
                  </div>
                  <div className="hidden md:block">
                    <p className="font-medium text-xs text-gray-500">
                      {offer.applicant?.email}
                    </p>
                  </div>
                  <div>
                    <p className="font-semibold text-sm text-gray-900">
                      {formatCurrency(offer.income)}
                    </p>
                  </div>
                  <div className="hidden md:block">
                    <span className="text-xs bg-cyan-100 text-cyan-700 border border-cyan-300 py-1 px-2 rounded-md capitalize">
                      {offer.service?.title || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className={`text-center text-[12px] border py-1 px-2 rounded-md capitalize ${getStatusBadge(offer.status)}`}>
                      {offer.status === 'DECLINED' ? 'Declined' : 
                       offer.status === 'ACCEPTED' ? 'Accepted' : 
                       offer.status === 'PENDING' ? 'Pending' : 
                       offer.status === 'WITHDRAWN' ? 'Withdrawn' : 
                       offer.status === 'CANCELLED' ? 'Cancelled' : 
                       offer.status === 'DRAFT' ? 'Draft' : 'Unknown'}
                    </span>
                  </div>
                  <div className="hidden md:block">
                    <p className="text-xs text-gray-500">
                      {formatDate(offer.date_published)}
                    </p>
                  </div>
                  <div className="relative md:col-span-0 col-span-1 justify-self-end">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleDropdown(offer.id);
                      }}
                      className="text-gray-600 hover:text-gray-900 p-2"
                    >
                      ⋮
                    </button>
                    {dropdownOpen === offer.id && (
                      <div className="absolute right-0 top-8 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-10">
                        <div className="py-1">
                          <button
                            onClick={() => handleEditOffer(offer.id)}
                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            Edit Offer
                          </button>
                          <button
                            onClick={() => navigate(`/job_applicants/${offer.job.id}`)}
                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            View Applicants
                          </button>
                          <button
                            onClick={() => handleViewCandidate(offer.applicant?.id)}
                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            View Candidate
                          </button>
                          {offer.status !== 'WITHDRAWN' && offer.status !== 'CANCELLED' && (
                            <button
                              onClick={() => handleWithdrawOffer(offer.id)}
                              className="block w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                            >
                              Withdraw Offer
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientOffers;